import { useEffect, useMemo, useRef, useState } from "react";
import { recognizeIdentityDocumentImages } from "../../utils/identityDocumentOcr";

function cleanText(value) {
  return String(value || "").trim();
}

function displayDate(value) {
  const match = cleanText(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : cleanText(value);
}

function resultRows(result) {
  const customer = result?.customer || {};
  const document = result?.document || {};
  return [
    ["Cognome", customer.surname],
    ["Nome", customer.name],
    ["Sesso", customer.gender],
    ["Data nascita", displayDate(customer.birthDate)],
    ["Luogo nascita", customer.birthPlace],
    ["Codice fiscale", customer.fiscalCode],
    ["Cittadinanza", customer.nationalityText || customer.nationalityIso3],
    ["Indirizzo / Residenza", customer.residenceText],
    ["Tipo documento", document.documentType],
    ["Numero documento", document.documentNumber],
    ["Data rilascio", displayDate(document.documentIssueDate)],
    ["Data scadenza", displayDate(document.documentExpiryDate)],
    ["Luogo rilascio", document.documentIssuePlace],
    ["Ente rilascio", document.documentIssuer],
  ].filter(([, value]) => cleanText(value));
}

export default function CustomerDocumentScanner({ onApply, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState("");
  const [frontImage, setFrontImage] = useState("");
  const [backImage, setBackImage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [scanError, setScanError] = useState("");
  const rows = useMemo(() => resultRows(result), [result]);

  useEffect(() => {
    let disposed = false;
    async function startCamera() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("Fotocamera non disponibile. Apri VestaMNG tramite HTTPS oppure localhost e verifica che il browser consenta l'accesso alla fotocamera.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            facingMode: { ideal: "environment" },
          },
        });
        if (disposed) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (error) {
        const denied = error?.name === "NotAllowedError" || error?.name === "SecurityError";
        setCameraError(denied ? "Accesso alla fotocamera negato. Abilita il permesso della fotocamera per VestaMNG e riapri lo scanner." : "Impossibile avviare la fotocamera del PC.");
      }
    }
    startCamera();
    return () => {
      disposed = true;
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  function captureFrame(side) {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      setScanError("La fotocamera non è ancora pronta.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d", { alpha: false });
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL("image/jpeg", 0.94);
    if (side === "front") setFrontImage(image);
    else setBackImage(image);
    setResult(null);
    setScanError("");
  }

  async function recognize() {
    if (!frontImage && !backImage) {
      setScanError("Acquisisci almeno il fronte o il retro del documento.");
      return;
    }
    setProcessing(true);
    setProgress(0);
    setResult(null);
    setScanError("");
    try {
      const parsed = await recognizeIdentityDocumentImages([frontImage, backImage].filter(Boolean), setProgress);
      setResult(parsed);
      if (!resultRows(parsed).length) setScanError("Il documento è stato letto, ma non sono stati riconosciuti dati affidabili. Migliora luce e messa a fuoco e riprova.");
    } catch (error) {
      setScanError(error?.message || "Impossibile riconoscere il documento.");
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="identity_document_scanner_backdrop" role="dialog" aria-modal="true" aria-label="Scansione documento identità">
      <div className="overlay_card identity_document_scanner_card">
        <div className="overlay_header">
          <h2>SCANSIONE DOCUMENTO IDENTITÀ</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi scanner" disabled={processing}>×</button>
        </div>
        <div className="overlay_body identity_document_scanner_body">
          <div className="identity_document_camera_panel">
            <div className="identity_document_camera_view">
              <video ref={videoRef} autoPlay muted playsInline />
              <div className="identity_document_camera_frame" aria-hidden="true"></div>
              <div className="identity_document_camera_hint">Centra il documento, evita riflessi e mantieni il testo a fuoco.</div>
            </div>
            {cameraError ? <div className="identity_document_scanner_error">{cameraError}</div> : null}
            <div className="identity_document_capture_actions">
              <button type="button" className="identity_document_capture_button" onClick={() => captureFrame("front")} disabled={Boolean(cameraError) || processing}>Acquisisci fronte</button>
              <button type="button" className="identity_document_capture_button" onClick={() => captureFrame("back")} disabled={Boolean(cameraError) || processing}>Acquisisci retro</button>
            </div>
            <div className="identity_document_capture_previews">
              <div className={`identity_document_capture_preview${frontImage ? " is_captured" : ""}`}>
                <span>Fronte</span>
                {frontImage ? <img src={frontImage} alt="Fronte documento acquisito" /> : <div>Nessuna acquisizione</div>}
              </div>
              <div className={`identity_document_capture_preview${backImage ? " is_captured" : ""}`}>
                <span>Retro</span>
                {backImage ? <img src={backImage} alt="Retro documento acquisito" /> : <div>Nessuna acquisizione</div>}
              </div>
            </div>
          </div>

          <div className="identity_document_result_panel">
            <div className="identity_document_result_header">
              <h3>Dati riconosciuti</h3>
              <button type="button" className="identity_document_recognize_button" onClick={recognize} disabled={processing || (!frontImage && !backImage)}>{processing ? "Riconoscimento..." : "Riconosci dati"}</button>
            </div>
            {processing ? (
              <div className="identity_document_progress_wrap">
                <progress max="1" value={Math.max(0, Math.min(1, progress))}></progress>
                <span>{Math.round(Math.max(0, Math.min(1, progress)) * 100)}%</span>
              </div>
            ) : null}
            {scanError ? <div className="identity_document_scanner_error">{scanError}</div> : null}
            {!processing && !result ? <div className="identity_document_result_empty">Acquisisci il documento e premi “Riconosci dati”.</div> : null}
            {result ? (
              <div className="identity_document_result_list">
                {rows.map(([label, value]) => <div className="identity_document_result_row" key={label}><span>{label}</span><strong>{value}</strong></div>)}
                {!rows.length ? <div className="identity_document_result_empty">Nessun dato affidabile riconosciuto.</div> : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="overlay_footer identity_document_scanner_footer">
          <div className="identity_document_privacy_note">Le immagini vengono elaborate nel browser e non vengono salvate nell’anagrafica.</div>
          <button type="button" className="overlay_save_button" onClick={() => onApply?.(result)} disabled={!result || !rows.length || processing}>Applica dati riconosciuti</button>
        </div>
      </div>
    </div>
  );
}
